'use client';

/**
 * ChronicleBulkSyncProvider — initializes the session creation engine and
 * wires up bulk sync auto-trigger and event listeners.
 *
 * Startup sequence (T030):
 * 1. Clean up temp dir (crash recovery)
 * 2. Start session creation engine
 * 3. Provide auth token to engine
 * 4. Subscribe to bulk_download:* and session_engine:* events
 * 5. Watch device connection for auto-trigger
 */
import { useAuthToken } from '@/hooks/useAuthToken';
import { useCallback, useEffect, useMemo, useRef, type ReactNode } from 'react';

import { useAtomValue } from 'jotai';

import { useChronicleBulkSyncEvents } from '@/app-shell/chronicle/hooks/useChronicleBulkSyncEvents';
import { useDesktopChunkUploadConcurrency } from '@/app-shell/flags/chunk-upload-concurrency';
import { useIsAppsUseTranscriptionServiceV2DesktopEnabled } from '@/app-shell/flags/desktop-flags';
import { useIsAppsRustAuth0TokenRefreshDesktopEnabled } from '@/app-shell/flags/rust-auth0-token-refresh-desktop';
import {
  chronicleDeviceStateAtom,
  chronicleOnboardingModalOpenAtom,
} from '@/atoms/chronicle/connection';
import { getDomains } from '@/helpers/getDomains';
import { useUser } from '@/hooks/query/useQueryUser';
import { useCurrentInputLanguages } from '@/hooks/useCurrentInputLanguages';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { setChronicleActiveUser } from '@/tauri/chronicle/active-user';
import { autoSessionBridge } from '@/tauri/chronicle/auto-session';
import { bulkSyncBridge, type MigrationOutcome } from '@/tauri/chronicle/bulk-sync';
import { chronicleSetupBridge } from '@/tauri/chronicle/setup';
import { setTranscriptionConfig } from '@/tauri/transcription/config';
import {
  resolveEffectiveTranscriptionService,
  TRANSCRIPTION_V2_FALLBACK_TAG,
} from '@/tauri/transcription/effective-service';
import { isTauri } from '@/tauri/utils';
import {
  trackHardwareBulkSyncDbMigrationApplied,
  trackHardwareBulkSyncDbMigrationFailed,
} from '@/utils/analytics/track-hardware-events';
import { logger } from '@/utils/logger/logger';
import { useAuthState } from '@heidi/auth/state';

import { useChronicleBulkSyncStartup } from './hooks/use-chronicle-bulk-sync-startup';
import { useChronicleObservabilityListener } from './hooks/use-chronicle-observability-listener';
import { useRecoveryDrain } from './hooks/use-recovery-drain';

interface ChronicleBulkSyncProviderProps {
  readonly children: ReactNode;
}

/** Translates a Rust `MigrationOutcome` into the appropriate analytics
 *  event(s). Pure helper — module-scoped to keep the effect body small. */
function reportMigrationOutcome(outcome: MigrationOutcome) {
  if (outcome.errorCode) {
    trackHardwareBulkSyncDbMigrationFailed(
      outcome.fromVersion,
      outcome.targetVersion,
      outcome.errorCode
    );
    logger.warn({
      msg: '[bulk_sync] DB migration failure surfaced',
      from: outcome.fromVersion,
      target: outcome.toVersion,
      errorCode: outcome.errorCode,
    });
    return;
  }
  if (outcome.fromVersion < outcome.toVersion) {
    trackHardwareBulkSyncDbMigrationApplied(outcome.fromVersion, outcome.toVersion);
    logger.info({
      msg: '[bulk_sync] DB migration applied',
      from: outcome.fromVersion,
      to: outcome.toVersion,
    });
  }
  // Idempotent reopen (from === to, no error) — intentionally silent.
}

export function ChronicleBulkSyncProvider({ children }: ChronicleBulkSyncProviderProps) {
  const { isAuthenticated } = useIsAuthenticated();
  const authState = useAuthState();
  const token = useAuthToken();
  const initialAuthProvisionedRef = useRef(false);
  const clearAuthPromiseRef = useRef<Promise<void> | null>(null);
  const migrationConsumedRef = useRef(false);
  const rustAuth0TokenRefresh = useIsAppsRustAuth0TokenRefreshDesktopEnabled();

  // APP-4647: consume the Rust-stashed migration outcome once on mount and
  // fan out analytics events. Independent of auth state — migrations run in
  // setup() before any user is logged in.
  useEffect(() => {
    if (!isTauri() || migrationConsumedRef.current) return;
    migrationConsumedRef.current = true;

    void (async () => {
      try {
        const dto = await bulkSyncBridge.consumeMigrationOutcome();
        if (!dto) return;
        if (dto.previousFailure) reportMigrationOutcome(dto.previousFailure);
        if (dto.current) reportMigrationOutcome(dto.current);
      } catch (error) {
        logger.warn({
          msg: '[bulk_sync] Failed to consume migration outcome',
          error: error instanceof Error ? error.message : String(error),
        });
      }
    })();
  }, []);

  // The imperative cache can be empty on the provider's first render; this
  // subscription re-renders as soon as the existing user query hydrates.
  const { data: currentUser } = useUser();
  const userId = currentUser?.user_ref_id ?? null;
  const { deviceName, serialNumber } = useAtomValue(chronicleDeviceStateAtom);
  const deviceSerial = serialNumber ?? deviceName;
  const isOnboardingModalOpen = useAtomValue(chronicleOnboardingModalOpenAtom);

  // Callbacks for the events hook to get fresh auth state
  const getToken = useCallback(() => token ?? null, [token]);
  const getBaseUrl = useCallback(() => {
    const { baseV2_1Url } = getDomains();
    // baseV2_1Url is like ".../api/v2-1/ml-scribe/"
    // The engine base_url should be the root (without the path suffix)
    const result = /^(https?:\/\/[^/]+)/.exec(baseV2_1Url);
    return result?.[1] ?? baseV2_1Url;
  }, []);

  const bulkSyncAuth = useMemo(
    () =>
      isAuthenticated && authState !== 'loading' && token && userId
        ? { token, userId }
        : null,
    [isAuthenticated, authState, token, userId]
  );
  const isBulkSyncAuthReady = bulkSyncAuth !== null;

  // Tell Rust who is signed in. The BLE actor resolves the binding code and
  // reconnect target from this on paths the frontend never sees, so it has to
  // be pushed rather than asked for. The loading state gates the initial
  // transient so a bootstrapping render can't push a spurious sign-out.
  useEffect(() => {
    if (!isTauri() || authState === 'loading') return;
    // `null` means signed out, and Rust acts on it — it tears the BLE link
    // down. An authenticated session whose user query has not hydrated yet
    // also has no `userId`, so pushing null there would disconnect a live
    // Remote and, arriving late, could undo a real sign-in push.
    if (isAuthenticated && !userId) return;
    setChronicleActiveUser(bulkSyncAuth?.userId ?? null).catch((error: unknown) => {
      // The connect paths push it themselves and surface their own failure;
      // this one keeps the actor current between connects.
      logger.warn({
        msg: '[bulk_sync] Failed to push the active user to Rust',
        error: error instanceof Error ? error.message : String(error),
      });
    });
  }, [authState, isAuthenticated, userId, bulkSyncAuth]);

  // Device-independent offline-cache recovery drain (launch + network reconnect).
  const runRecoveryDrain = useRecoveryDrain({
    bulkSyncAuth,
    isBulkSyncAuthReady,
    initialAuthProvisionedRef,
  });

  useChronicleBulkSyncStartup({
    bulkSyncAuth,
    authState,
    deviceSerial,
    getBaseUrl,
    runRecoveryDrain,
    initialAuthProvisionedRef,
    clearAuthPromiseRef,
    rustAuth0TokenRefresh,
  });

  // Clear Rust-side auto-trigger credentials as soon as auth is absent. This
  // prevents native BLE/USB events from starting sync while the login screen is visible.
  //
  // A SETTLED logged-out state must advance the account epoch, not just clear
  // the caches. A `provide_*` call dispatched for the account that just
  // logged out can still be in flight (e.g. a Rust-side Auth0 refresh) and,
  // without an epoch bump, would carry an epoch that still matches the live
  // one — so it can land after this clear (or after a *different* account
  // logs in next, since `tokenProvisionRef` resets to null on logout and the
  // following login just reads whatever epoch is current) and repopulate
  // native state with a stale credential. `accountTransition` performs the
  // plain-clear branch's three clears plus the note-defaults invalidation
  // (which only a real account change needs) atomically under its epoch bump.
  //
  // The still-authenticated shape gets the plain clear instead. This effect
  // fires on `authState !== 'loading' && !isBulkSyncAuthReady` — the cache's
  // own settled verdict, not `useIsAuthenticated()`'s isAuthenticated/isLoading
  // (a persisted token makes those provisionally true/false from first
  // render, before the cache has actually resolved anything). `bulkSyncAuth`
  // needs BOTH `token` (Redux) and `userId` (the react-query cache) — so
  // this also fires while
  // the user is very much still logged in and one of those is momentarily
  // falsy: a cache eviction or `queryClient.clear()`, an `isAuthenticated`
  // flicker, a token write that briefly blanks the slice. A plain clear is
  // self-healing there (the next task emits `token_needed` and gets the
  // token straight back), but an epoch bump is not: every queued
  // `SessionTask`, the running bulk-download, and the in-flight offline drain
  // fail their epoch check, and nothing re-stamps them. This is the same
  // settled-vs-transient distinction `use-chronicle-bulk-sync-startup.ts`
  // draws for `committedBulkSyncAuth`.
  useEffect(() => {
    if (!isTauri() || authState === 'loading' || isBulkSyncAuthReady) return;

    initialAuthProvisionedRef.current = false;
    const clearAuthPromise = (
      isAuthenticated
        ? Promise.all([
            bulkSyncBridge.clearToken(),
            bulkSyncBridge.clearAutoSyncAuth(),
            autoSessionBridge.clearAuth(),
          ]).then(() => undefined)
        : bulkSyncBridge.accountTransition()
    ).then(
      () => undefined,
      (error: unknown) => {
        logger.warn({
          msg: isAuthenticated
            ? '[bulk_sync] Failed to clear native credentials while auth was transiently incomplete'
            : '[bulk_sync] Failed to advance account epoch on logout',
          error: error instanceof Error ? error.message : String(error),
        });
      }
    );

    clearAuthPromiseRef.current = clearAuthPromise;
    void clearAuthPromise.finally(() => {
      if (clearAuthPromiseRef.current === clearAuthPromise) {
        clearAuthPromiseRef.current = null;
      }
    });
  }, [isAuthenticated, authState, isBulkSyncAuthReady]);

  // APP-3779: Push the current v2 transcription config to Rust state so the
  // chunked bulk-sync engine (`session_creation_engine::process_chunked_task`)
  // routes chunks to the right backend even when there's no active live
  // session. The live path also writes this state via `chronicle_session_start`
  // — the redundant writes are harmless (last-writer-wins on identical values).
  //
  // Triggers on mount and whenever the flag, the EKS base URL, or the user's
  // current input languages change. The flag itself rarely changes mid-session
  // but the languages do (user opens settings, picks a different language)
  // and we want bulk-sync sweeps that follow to use the new value.
  //
  // When a pushed value actually takes effect: `process_chunked_task` snapshots
  // `TranscriptionConfigState` once per chunked task, NOT mid-session. So a
  // sweep already in flight keeps the old value, and a live recording keeps it
  // until the next session — its `UploadClient` is locked at session start.
  // That is the answer during an incident when the question is "I changed it,
  // is the fleet on the new value yet?"
  const useTranscriptionServiceV2 = useIsAppsUseTranscriptionServiceV2DesktopEnabled();
  const inputLanguages = useCurrentInputLanguages();
  useEffect(() => {
    if (!isTauri()) return;
    // The rule lives on `resolveEffectiveTranscriptionService`. What is specific
    // to this writer: it is the only one that CAN store the contradictory pair.
    // The other two writers of this process-wide state reject flag-on-without-
    // a-URL at the IPC boundary; `set_transcription_config` takes what it is
    // given, and every later Rust-initiated upload — an offline-recording drain
    // included — reads what was pushed from here.
    const { effectiveUseV2, transcriptionBaseUrl, didFallBackToV1 } =
      resolveEffectiveTranscriptionService(useTranscriptionServiceV2);
    if (didFallBackToV1) {
      logger.warn({
        msg: '[transcription_config] v2 flag on but transcription base URL is null — pushing v1 so background syncs keep working',
        tag: TRANSCRIPTION_V2_FALLBACK_TAG,
        surface: 'chronicle_bulk_sync',
      });
    }
    setTranscriptionConfig({
      useTranscriptionServiceV2: effectiveUseV2,
      transcriptionBaseUrl,
      languages: inputLanguages,
    }).catch((error) => {
      logger.warn({
        msg: '[transcription_config] Failed to push v2 config to Rust',
        error: error instanceof Error ? error.message : String(error),
      });
    });
  }, [useTranscriptionServiceV2, inputLanguages]);

  // Push the remotely-configured chunk-upload concurrency into the engine so a
  // following bulk-sync sweep uses it. Re-pushed whenever the flag changes; the
  // engine applies it on the next task (floored at 1).
  const chunkUploadConcurrency = useDesktopChunkUploadConcurrency();
  useEffect(() => {
    if (!isTauri()) return;
    bulkSyncBridge.setChunkUploadConcurrency(chunkUploadConcurrency).catch((error) => {
      logger.warn({
        msg: '[session_engine] Failed to push chunk upload concurrency to Rust',
        error: error instanceof Error ? error.message : String(error),
      });
    });
  }, [chunkUploadConcurrency]);

  // APP-4868: Mirror UI signals that should defer background Chronicle
  // auto-actions into the cross-cutting `ChronicleSetupState` flag in Rust.
  // Today only the onboarding modal feeds this; future flows can be OR'd in
  // here without changing any consumer (e.g. `auto_bulk_sync`). The cleanup
  // forces the flag to `false` on unmount so an unexpected provider tear-down
  // mid-onboarding can't leave it stuck — without it the next auto-trigger
  // would be permanently suppressed until a WebView reload.
  useEffect(() => {
    if (!isTauri()) return;
    chronicleSetupBridge.setActive(isOnboardingModalOpen).catch((error) => {
      logger.warn({
        msg: '[chronicle/setup] Failed to set active flag',
        error: error instanceof Error ? error.message : String(error),
      });
    });
    return () => {
      chronicleSetupBridge.setActive(false).catch((error) => {
        logger.warn({
          msg: '[chronicle/setup] Failed to clear active flag on unmount',
          error: error instanceof Error ? error.message : String(error),
        });
      });
    };
  }, [isOnboardingModalOpen]);

  // Wire up event listeners (auto-trigger now lives in Rust — Phase 12)
  useChronicleBulkSyncEvents({
    enabled: isBulkSyncAuthReady,
    getToken,
    getBaseUrl,
  });
  useChronicleObservabilityListener();

  return <>{children}</>;
}
